import { motion } from 'framer-motion';
import { useInView } from '../hooks/useInView';

const SeoContent = () => {
  const [ref, isInView] = useInView({ threshold: 0.1 });

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section ref={ref} className="py-24 bg-gradient-to-b from-white to-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section principale - 2 colonnes */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-20">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              <strong className="text-teal-600">Réparation de Fenêtre</strong> à Toulouse : Votre Expert Local
            </h2>
            <p className="text-gray-600 mb-4 leading-relaxed">
              <strong>FenetrePro Toulouse</strong> est votre spécialiste en <a href="#services" onClick={(e) => { e.preventDefault(); scrollToSection('services'); }} className="text-teal-600 hover:text-teal-700 font-semibold cursor-pointer">réparation de fenêtres</a> dans la région toulousaine. Depuis plus de 15 ans, nous intervenons auprès des particuliers et professionnels pour tous types de dépannages et réparations de <a href="#services" onClick={(e) => { e.preventDefault(); scrollToSection('services'); }} className="text-teal-600 hover:text-teal-700 font-semibold cursor-pointer">fenêtre, baie vitrée et volet roulant</a>.
            </p>
            <p className="text-gray-600 mb-4 leading-relaxed">
              Notre expertise couvre la <strong>réparation de fenêtre PVC</strong>, la <strong>réparation de fenêtre bois</strong>, la <strong>réparation de fenêtre aluminium</strong>, ainsi que le remplacement de vitres cassées et la réparation de mécanismes défectueux. Nous proposons également des services de <strong>réparation de baie vitrée</strong> et de <strong>dépannage de volet roulant</strong> à Toulouse et dans toute la <strong>Haute-Garonne</strong>.
            </p>
            <p className="text-gray-600 mb-4 leading-relaxed">
              Que vous ayez besoin d'une <strong>réparation de fenêtre en urgence</strong>, d'un remplacement de double vitrage, ou d'une restauration complète de vos menuiseries, nos <a href="#team" onClick={(e) => { e.preventDefault(); scrollToSection('team'); }} className="text-teal-600 hover:text-teal-700 font-semibold cursor-pointer">artisans qualifiés</a> interviennent rapidement avec du matériel professionnel et des pièces de qualité. Nous garantissons toutes nos interventions pendant 2 ans pour votre tranquillité d'esprit.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="relative"
          >
            <div className="relative w-full h-[500px] rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="https://public.readdy.ai/ai/img_res/fe73af6e2828b47ac231ab05cc1f4cae.jpg"
                alt="Vue de Toulouse - Réparation fenêtres Haute-Garonne"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-teal-900/60 via-transparent to-transparent"></div>
              
              {/* Badge localisation */}
              <div className="absolute bottom-6 left-6 bg-white rounded-xl shadow-xl p-4">
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mr-3 flex-shrink-0">
                    <i className="ri-map-pin-line text-teal-600 text-2xl"></i>
                  </div>
                  <div>
                    <p className="text-lg font-bold text-gray-900">Toulouse</p>
                    <p className="text-sm text-gray-600">Haute-Garonne (31)</p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Section Pourquoi nous choisir - 2 colonnes */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-20">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="relative"
          >
            <div className="relative w-full h-[500px] rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="https://readdy.ai/api/search-image?query=professional%20window%20repair%20specialist%20technician%20working%20on%20modern%20residential%20window%20installation%20using%20precision%20tools%20and%20equipment%20demonstrating%20expert%20craftsmanship%20quality%20service%20attention%20to%20detail%20in%20bright%20contemporary%20home%20interior%20setting%20with%20natural%20daylight%20clean%20workspace%20professional%20atmosphere&width=800&height=600&seq=window-repair-work-v1&orientation=landscape"
                alt="Artisan vitrier professionnel Toulouse"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-orange-900/50 via-transparent to-transparent"></div>
              
              {/* Badge certification */}
              <div className="absolute top-6 right-6 bg-white rounded-xl shadow-xl p-4">
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center mr-3 flex-shrink-0">
                    <i className="ri-award-line text-orange-600 text-xl"></i>
                  </div>
                  <div>
                    <p className="text-sm font-bold text-gray-900">Certifié RGE</p>
                    <p className="text-xs text-gray-600">Qualibat</p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">
              Pourquoi Choisir <strong className="text-teal-600">FenetrePro</strong> à Toulouse ?
            </h3>
            <div className="space-y-4">
              <div className="flex items-start">
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                  <i className="ri-time-line text-2xl text-teal-600"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">
                    <a href="#contact" onClick={(e) => { e.preventDefault(); scrollToSection('contact'); }} className="hover:text-teal-600 cursor-pointer">Intervention Rapide 24h/24</a>
                  </h4>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    Service d'urgence disponible pour tous vos <a href="#services" onClick={(e) => { e.preventDefault(); scrollToSection('services'); }} className="text-teal-600 hover:underline cursor-pointer">dépannages de fenêtre</a> à Toulouse et dans toute la Haute-Garonne. Nous intervenons dans les 24 heures pour sécuriser votre domicile en cas de vitre cassée ou de problème de fermeture.
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                  <i className="ri-shield-check-line text-2xl text-orange-600"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Garantie 2 Ans sur Toutes Réparations</h4>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    Toutes nos <a href="#services" onClick={(e) => { e.preventDefault(); scrollToSection('services'); }} className="text-teal-600 hover:underline cursor-pointer">réparations de fenêtre</a> sont garanties 2 ans, pièces et main-d'œuvre. Nous utilisons uniquement des matériaux de qualité professionnelle pour assurer la durabilité de nos interventions à Toulouse.
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                  <i className="ri-user-star-line text-2xl text-teal-600"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">
                    <a href="#team" onClick={(e) => { e.preventDefault(); scrollToSection('team'); }} className="hover:text-teal-600 cursor-pointer">Artisans Qualifiés RGE</a>
                  </h4>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    Notre <a href="#team" onClick={(e) => { e.preventDefault(); scrollToSection('team'); }} className="text-teal-600 hover:underline cursor-pointer">équipe d'artisans vitriers</a> est formée aux dernières techniques de réparation et dispose de plus de 15 ans d'expérience dans la réparation de fenêtre à Toulouse et ses environs. Certification RGE Qualibat.
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                  <i className="ri-file-text-line text-2xl text-orange-600"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">
                    <a href="#contact" onClick={(e) => { e.preventDefault(); scrollToSection('contact'); }} className="hover:text-teal-600 cursor-pointer">Devis Gratuit Sans Engagement</a>
                  </h4>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    Nous vous proposons un <a href="#contact" onClick={(e) => { e.preventDefault(); scrollToSection('contact'); }} className="text-teal-600 hover:underline cursor-pointer">devis gratuit et détaillé</a> pour tous vos projets de réparation de fenêtre à Toulouse. Nos tarifs sont transparents et compétitifs, sans frais cachés. Réponse sous 24h.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Section Zone d'intervention - 2 colonnes */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-20">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">
              <strong className="text-teal-600">Zone d'Intervention</strong> : Toute la Haute-Garonne
            </h3>
            <p className="text-gray-600 mb-6 leading-relaxed">
              <strong>FenetrePro</strong> intervient dans <strong>toute la Haute-Garonne (31)</strong> et <strong>toutes ses villes</strong> pour vos besoins en <a href="#services" onClick={(e) => { e.preventDefault(); scrollToSection('services'); }} className="text-teal-600 hover:text-teal-700 font-semibold cursor-pointer">réparation de fenêtre</a>, <strong>dépannage de vitrerie</strong> et <a href="#services" onClick={(e) => { e.preventDefault(); scrollToSection('services'); }} className="text-teal-600 hover:text-teal-700 font-semibold cursor-pointer">réparation de volet roulant</a>. Des grandes villes aux petites communes rurales, nous assurons une <strong>couverture complète du département</strong> avec des interventions rapides partout en Haute-Garonne.
            </p>
            
            <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
              <h4 className="font-bold text-gray-900 mb-4 flex items-center">
                <i className="ri-map-2-line text-teal-600 mr-2 text-xl"></i>
                Toutes les Villes de Haute-Garonne Desservies
              </h4>
              <p className="text-sm text-gray-600 mb-4">
                Nous intervenons dans <strong>les 589 communes de la Haute-Garonne</strong>, incluant notamment :
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div className="flex items-center text-sm text-gray-700">
                  <i className="ri-checkbox-circle-fill text-teal-600 mr-2"></i>
                  <a href="#contact" onClick={(e) => { e.preventDefault(); scrollToSection('contact'); }} className="hover:text-teal-600 cursor-pointer">Toulouse</a>
                </div>
                <div className="flex items-center text-sm text-gray-700">
                  <i className="ri-checkbox-circle-fill text-orange-600 mr-2"></i>
                  <a href="#contact" onClick={(e) => { e.preventDefault(); scrollToSection('contact'); }} className="hover:text-teal-600 cursor-pointer">Colomiers</a>
                </div>
                <div className="flex items-center text-sm text-gray-700">
                  <i className="ri-checkbox-circle-fill text-teal-600 mr-2"></i>
                  <a href="#contact" onClick={(e) => { e.preventDefault(); scrollToSection('contact'); }} className="hover:text-teal-600 cursor-pointer">Tournefeuille</a>
                </div>
                <div className="flex items-center text-sm text-gray-700">
                  <i className="ri-checkbox-circle-fill text-orange-600 mr-2"></i>
                  <a href="#contact" onClick={(e) => { e.preventDefault(); scrollToSection('contact'); }} className="hover:text-teal-600 cursor-pointer">Blagnac</a>
                </div>
                <div className="flex items-center text-sm text-gray-700">
                  <i className="ri-checkbox-circle-fill text-teal-600 mr-2"></i>
                  <a href="#contact" onClick={(e) => { e.preventDefault(); scrollToSection('contact'); }} className="hover:text-teal-600 cursor-pointer">Muret</a>
                </div>
                <div className="flex items-center text-sm text-gray-700">
                  <i className="ri-checkbox-circle-fill text-orange-600 mr-2"></i>
                  <a href="#contact" onClick={(e) => { e.preventDefault(); scrollToSection('contact'); }} className="hover:text-teal-600 cursor-pointer">Balma</a>
                </div>
                <div className="flex items-center text-sm text-gray-700">
                  <i className="ri-checkbox-circle-fill text-teal-600 mr-2"></i>
                  <a href="#contact" onClick={(e) => { e.preventDefault(); scrollToSection('contact'); }} className="hover:text-teal-600 cursor-pointer">Ramonville</a>
                </div>
                <div className="flex items-center text-sm text-gray-700">
                  <i className="ri-checkbox-circle-fill text-orange-600 mr-2"></i>
                  <a href="#contact" onClick={(e) => { e.preventDefault(); scrollToSection('contact'); }} className="hover:text-teal-600 cursor-pointer">Cugnaux</a>
                </div>
                <div className="flex items-center text-sm text-gray-700">
                  <i className="ri-checkbox-circle-fill text-teal-600 mr-2"></i>
                  <a href="#contact" onClick={(e) => { e.preventDefault(); scrollToSection('contact'); }} className="hover:text-teal-600 cursor-pointer">L'Union</a>
                </div>
                <div className="flex items-center text-sm text-gray-700">
                  <i className="ri-checkbox-circle-fill text-orange-600 mr-2"></i>
                  <a href="#contact" onClick={(e) => { e.preventDefault(); scrollToSection('contact'); }} className="hover:text-teal-600 cursor-pointer">Saint-Gaudens</a>
                </div>
                <div className="flex items-center text-sm text-gray-700">
                  <i className="ri-checkbox-circle-fill text-teal-600 mr-2"></i>
                  <a href="#contact" onClick={(e) => { e.preventDefault(); scrollToSection('contact'); }} className="hover:text-teal-600 cursor-pointer">Plaisance-du-Touch</a>
                </div>
                <div className="flex items-center text-sm text-gray-700">
                  <i className="ri-checkbox-circle-fill text-orange-600 mr-2"></i>
                  <a href="#contact" onClick={(e) => { e.preventDefault(); scrollToSection('contact'); }} className="hover:text-teal-600 cursor-pointer">Saint-Orens</a>
                </div>
                <div className="flex items-center text-sm text-gray-700">
                  <i className="ri-checkbox-circle-fill text-teal-600 mr-2"></i>
                  <a href="#contact" onClick={(e) => { e.preventDefault(); scrollToSection('contact'); }} className="hover:text-teal-600 cursor-pointer">Portet-sur-Garonne</a>
                </div>
                <div className="flex items-center text-sm text-gray-700">
                  <i className="ri-checkbox-circle-fill text-orange-600 mr-2"></i>
                  <a href="#contact" onClick={(e) => { e.preventDefault(); scrollToSection('contact'); }} className="hover:text-teal-600 cursor-pointer">Auterive</a>
                </div>
                <div className="flex items-center text-sm text-gray-700">
                  <i className="ri-checkbox-circle-fill text-teal-600 mr-2"></i>
                  <a href="#contact" onClick={(e) => { e.preventDefault(); scrollToSection('contact'); }} className="hover:text-teal-600 cursor-pointer">Revel</a>
                </div>
                <div className="flex items-center text-sm text-gray-700">
                  <i className="ri-checkbox-circle-fill text-orange-600 mr-2"></i>
                  <a href="#contact" onClick={(e) => { e.preventDefault(); scrollToSection('contact'); }} className="hover:text-teal-600 cursor-pointer">Carbonne</a>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-4 italic">
                Et toutes les autres communes de la Haute-Garonne...
              </p>
            </div>

            <div className="bg-gradient-to-r from-teal-50 to-orange-50 rounded-xl p-6">
              <div className="flex items-start">
                <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center mr-4 flex-shrink-0 shadow-md">
                  <i className="ri-phone-line text-teal-600 text-2xl"></i>
                </div>
                <div>
                  <p className="font-semibold text-gray-900 mb-1">Besoin d'une intervention dans votre ville ?</p>
                  <p className="text-sm text-gray-600 mb-3">
                    Où que vous soyez en Haute-Garonne, <a href="#contact" onClick={(e) => { e.preventDefault(); scrollToSection('contact'); }} className="text-teal-600 hover:underline cursor-pointer">contactez-nous pour un devis gratuit</a>
                  </p>
                  <a href="tel:0554542545" className="text-teal-600 font-bold text-lg hover:text-teal-700 cursor-pointer">
                    05 54 54 25 45
                  </a>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="relative"
          >
            <div className="relative w-full h-[600px] rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="https://readdy.ai/api/search-image?query=detailed%20map%20of%20toulouse%20metropolitan%20area%20haute%20garonne%20region%20showing%20major%20districts%20neighborhoods%20suburbs%20colomiers%20blagnac%20tournefeuille%20muret%20with%20clear%20street%20layout%20urban%20planning%20geographic%20zones%20administrative%20boundaries%20modern%20cartography%20style%20clean%20professional%20design%20with%20color%20coded%20areas&width=800&height=900&seq=toulouse-map-v1&orientation=portrait"
                alt="Carte zone intervention Toulouse Haute-Garonne"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-gray-900/70 via-transparent to-transparent"></div>
              
              {/* Overlay info */}
              <div className="absolute bottom-6 left-6 right-6">
                <div className="bg-white/95 backdrop-blur-sm rounded-xl p-6">
                  <h4 className="font-bold text-gray-900 mb-3 flex items-center">
                    <i className="ri-map-pin-range-line text-teal-600 mr-2 text-xl"></i>
                    Couverture Complète
                  </h4>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    Nous intervenons dans <strong>toute la Haute-Garonne (31)</strong> et <strong>toutes ses villes</strong> pour tous vos besoins en réparation de fenêtres, vitrerie et menuiserie. Service d'urgence disponible 24h/24 dans l'ensemble du département.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Section Services détaillés */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="bg-gradient-to-r from-teal-50 to-orange-50 rounded-2xl p-8 lg:p-12"
        >
          <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4 text-center">
            Nos Services de <strong className="text-teal-600">Réparation de Fenêtre</strong> à Toulouse
          </h3>
          <p className="text-gray-600 text-center mb-8 leading-relaxed max-w-4xl mx-auto">
            <a href="#about" onClick={(e) => { e.preventDefault(); scrollToSection('about'); }} className="text-teal-600 hover:text-teal-700 font-semibold cursor-pointer">Vitrier professionnel à Toulouse</a>, nous intervenons pour la <strong>réparation de fenêtre PVC</strong>, <strong>réparation de fenêtre bois</strong> et <strong>réparation de fenêtre aluminium</strong>, le remplacement de vitres cassées, la <a href="#services" onClick={(e) => { e.preventDefault(); scrollToSection('services'); }} className="text-teal-600 hover:underline cursor-pointer">réparation de baie vitrée coulissante</a>, le <a href="#services" onClick={(e) => { e.preventDefault(); scrollToSection('services'); }} className="text-teal-600 hover:underline cursor-pointer">dépannage de volet roulant</a> électrique et manuel, l'installation de double vitrage, la restauration de fenêtres anciennes, et tous travaux de vitrerie. Service d'urgence disponible 24h/24 pour sécuriser votre domicile dans toute la Haute-Garonne.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <a href="#contact" onClick={(e) => { e.preventDefault(); scrollToSection('contact'); }} className="bg-white rounded-xl p-6 shadow-md hover:shadow-xl transition-shadow cursor-pointer">
              <div className="w-14 h-14 bg-teal-100 rounded-xl flex items-center justify-center mb-4 flex-shrink-0">
                <i className="ri-window-line text-teal-600 text-2xl"></i>
              </div>
              <h4 className="font-bold text-gray-900 mb-2">Réparation Fenêtre PVC</h4>
              <p className="text-sm text-gray-600">Remplacement de joints, mécanismes, vitres et réglages pour fenêtre PVC à Toulouse</p>
            </a>

            <a href="#contact" onClick={(e) => { e.preventDefault(); scrollToSection('contact'); }} className="bg-white rounded-xl p-6 shadow-md hover:shadow-xl transition-shadow cursor-pointer">
              <div className="w-14 h-14 bg-orange-100 rounded-xl flex items-center justify-center mb-4 flex-shrink-0">
                <i className="ri-home-4-line text-orange-600 text-2xl"></i>
              </div>
              <h4 className="font-bold text-gray-900 mb-2">Réparation Fenêtre Bois</h4>
              <p className="text-sm text-gray-600">Restauration, traitement et réparation de fenêtre bois ancienne et moderne</p>
            </a>

            <a href="#contact" onClick={(e) => { e.preventDefault(); scrollToSection('contact'); }} className="bg-white rounded-xl p-6 shadow-md hover:shadow-xl transition-shadow cursor-pointer">
              <div className="w-14 h-14 bg-teal-100 rounded-xl flex items-center justify-center mb-4 flex-shrink-0">
                <i className="ri-building-line text-teal-600 text-2xl"></i>
              </div>
              <h4 className="font-bold text-gray-900 mb-2">Réparation Fenêtre Alu</h4>
              <p className="text-sm text-gray-600">Dépannage et réparation de fenêtre aluminium, baie vitrée coulissante</p>
            </a>

            <a href="#contact" onClick={(e) => { e.preventDefault(); scrollToSection('contact'); }} className="bg-white rounded-xl p-6 shadow-md hover:shadow-xl transition-shadow cursor-pointer">
              <div className="w-14 h-14 bg-orange-100 rounded-xl flex items-center justify-center mb-4 flex-shrink-0">
                <i className="ri-layout-grid-line text-orange-600 text-2xl"></i>
              </div>
              <h4 className="font-bold text-gray-900 mb-2">Remplacement Vitres</h4>
              <p className="text-sm text-gray-600">Remplacement de vitre cassée, double vitrage, survitrage à Toulouse</p>
            </a>

            <a href="#contact" onClick={(e) => { e.preventDefault(); scrollToSection('contact'); }} className="bg-white rounded-xl p-6 shadow-md hover:shadow-xl transition-shadow cursor-pointer">
              <div className="w-14 h-14 bg-teal-100 rounded-xl flex items-center justify-center mb-4 flex-shrink-0">
                <i className="ri-door-line text-teal-600 text-2xl"></i>
              </div>
              <h4 className="font-bold text-gray-900 mb-2">Dépannage Volet Roulant</h4>
              <p className="text-sm text-gray-600">Réparation volet roulant électrique et manuel, motorisation</p>
            </a>

            <a href="#contact" onClick={(e) => { e.preventDefault(); scrollToSection('contact'); }} className="bg-white rounded-xl p-6 shadow-md hover:shadow-xl transition-shadow cursor-pointer">
              <div className="w-14 h-14 bg-orange-100 rounded-xl flex items-center justify-center mb-4 flex-shrink-0">
                <i className="ri-tools-line text-orange-600 text-2xl"></i>
              </div>
              <h4 className="font-bold text-gray-900 mb-2">Dépannage Urgence 24h</h4>
              <p className="text-sm text-gray-600">Intervention rapide pour sécurisation et réparation d'urgence</p>
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default SeoContent;
